import { Dialog } from '@headlessui/react';
import { useState, useRef } from 'react';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { 
  XIcon, 
  DownloadIcon, 
  UploadIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationIcon
} from '@heroicons/react/outline';

export default function BulkUploadModal({ isOpen, onClose, onSuccess }) {
  const { t } = useLanguage();
  const [selectedFile, setSelectedFile] = useState(null);
  const [policyType, setPolicyType] = useState('mandatory-car');
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [step, setStep] = useState('select'); // select, validate, review, complete
  const fileInputRef = useRef(null);

  const policyTypes = [
    { value: 'mandatory-car', label: t('policies.mandatoryCarInsurance') },
    { value: 'tpl', label: t('policies.thirdPartyLiability') },
    { value: 'cargo', label: t('policies.cargoInsurance') }
  ];

  const handleClose = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setUploadResult(null);
    setStep('select');
    setPolicyType('mandatory-car');
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/api/bulk-upload/template/${policyType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulk-upload-template-${policyType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('bulkUpload.templateDownloaded'));
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(t('bulkUpload.templateDownloadError'));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        toast.error(t('bulkUpload.onlyExcelFiles'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('bulkUpload.fileTooLarge'));
        return;
      }
      setSelectedFile(file);
      setValidationResult(null);
      setUploadResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (!file.name.endsWith('.xlsx')) {
        toast.error(t('bulkUpload.onlyExcelFiles'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('bulkUpload.fileTooLarge'));
        return;
      }
      setSelectedFile(file);
      setValidationResult(null);
      setUploadResult(null);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      toast.error(t('bulkUpload.pleaseSelectFile'));
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('policyType', policyType);

      const response = await api.post('/api/bulk-upload/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setValidationResult(response.data);
      
      if (response.data.canProceed) {
        setStep('review');
        toast.success(t('bulkUpload.validationSuccess'));
      } else {
        setStep('validate');
        toast.warning(t('bulkUpload.validationErrors'));
      }
    } catch (error) {
      console.error('Error validating file:', error);
      toast.error(error.response?.data?.message || t('bulkUpload.validationError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleProcess = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('policyType', policyType);

      const response = await api.post('/api/bulk-upload/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult(response.data);
      setStep('complete');
      
      if (response.data.summary.failedCount === 0) {
        toast.success(t('bulkUpload.uploadSuccess'));
        if (onSuccess) onSuccess();
      } else {
        toast.warning(t('bulkUpload.uploadPartialSuccess'));
      }
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error(error.response?.data?.message || t('bulkUpload.uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-4xl w-full rounded-lg bg-white p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-gray-900">
              {t('bulkUpload.title')}
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className={`flex-1 text-center ${step !== 'select' ? 'opacity-50' : ''}`}>
                <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center ${
                  step === 'select' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  1
                </div>
                <p className="mt-2 text-sm font-medium">{t('bulkUpload.selectFile')}</p>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-2"></div>
              <div className={`flex-1 text-center ${step !== 'validate' && step !== 'review' ? 'opacity-50' : ''}`}>
                <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center ${
                  step === 'validate' || step === 'review' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  2
                </div>
                <p className="mt-2 text-sm font-medium">{t('bulkUpload.validate')}</p>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-2"></div>
              <div className={`flex-1 text-center ${step !== 'complete' ? 'opacity-50' : ''}`}>
                <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center ${
                  step === 'complete' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  3
                </div>
                <p className="mt-2 text-sm font-medium">{t('bulkUpload.complete')}</p>
              </div>
            </div>
          </div>

          {/* Select File Step */}
          {step === 'select' && (
            <div className="space-y-6">
              {/* Policy Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bulkUpload.policyType')}
                </label>
                <select
                  value={policyType}
                  onChange={(e) => setPolicyType(e.target.value)}
                  className="input"
                >
                  {policyTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Download Template */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">
                  {t('bulkUpload.step1')}
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  {t('bulkUpload.downloadTemplateDescription')}
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="btn-primary inline-flex items-center"
                >
                  <DownloadIcon className="h-5 w-5 mr-2" />
                  {t('bulkUpload.downloadTemplate')}
                </button>
              </div>

              {/* File Upload Area */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  {t('bulkUpload.step2')}
                </h4>
                <div
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary-500 transition-colors"
                >
                  <UploadIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  {selectedFile ? (
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">
                        {t('bulkUpload.dragDropOrClick')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('bulkUpload.excelOnly')}
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button onClick={handleClose} className="btn-secondary">
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleValidate}
                  disabled={!selectedFile || isUploading}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? t('bulkUpload.validating') : t('bulkUpload.validateFile')}
                </button>
              </div>
            </div>
          )}

          {/* Validation Results Step */}
          {(step === 'validate' || step === 'review') && validationResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {validationResult.summary.totalRows}
                  </p>
                  <p className="text-sm text-gray-600">{t('bulkUpload.totalRows')}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {validationResult.summary.validRows}
                  </p>
                  <p className="text-sm text-gray-600">{t('bulkUpload.validRows')}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {validationResult.summary.invalidRows}
                  </p>
                  <p className="text-sm text-gray-600">{t('bulkUpload.invalidRows')}</p>
                </div>
              </div>

              {/* Error Details */}
              {validationResult.summary.invalidRows > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <h4 className="font-medium text-red-900 mb-3 flex items-center">
                    <XCircleIcon className="h-5 w-5 mr-2" />
                    {t('bulkUpload.validationErrors')}
                  </h4>
                  <div className="space-y-3">
                    {validationResult.validationResults
                      .filter(r => !r.isValid)
                      .map((result, idx) => (
                        <div key={idx} className="bg-white rounded p-3">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {t('bulkUpload.row')} {result.row}: {result.data['Policy Number*']}
                          </p>
                          <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                            {result.errors.map((error, errIdx) => (
                              <li key={errIdx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setStep('select');
                    setValidationResult(null);
                  }}
                  className="btn-secondary"
                >
                  {t('common.back')}
                </button>
                <div className="flex space-x-3">
                  <button onClick={handleClose} className="btn-secondary">
                    {t('common.cancel')}
                  </button>
                  {validationResult.canProceed && (
                    <button
                      onClick={handleProcess}
                      disabled={isUploading}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? t('bulkUpload.processing') : t('bulkUpload.createPolicies')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && uploadResult && (
            <div className="space-y-6">
              {/* Success Summary */}
              <div className="text-center">
                {uploadResult.summary.failedCount === 0 ? (
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                ) : (
                  <ExclamationIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {uploadResult.summary.failedCount === 0 
                    ? t('bulkUpload.allPoliciesCreated')
                    : t('bulkUpload.somePoliciesFailed')}
                </h3>
                <p className="text-gray-600">
                  {t('bulkUpload.successCount')}: {uploadResult.summary.successCount} / {uploadResult.summary.totalRows}
                </p>
              </div>

              {/* Results Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {uploadResult.summary.successCount}
                  </p>
                  <p className="text-sm text-gray-600">{t('bulkUpload.policiesCreated')}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">
                    {uploadResult.summary.failedCount}
                  </p>
                  <p className="text-sm text-gray-600">{t('bulkUpload.policiesFailed')}</p>
                </div>
              </div>

              {/* Failed Policies Details */}
              {uploadResult.results.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                  <h4 className="font-medium text-red-900 mb-3">
                    {t('bulkUpload.failedPolicies')}
                  </h4>
                  <div className="space-y-2">
                    {uploadResult.results.failed.map((failure, idx) => (
                      <div key={idx} className="bg-white rounded p-3">
                        <p className="text-sm font-medium text-gray-900">
                          {t('bulkUpload.row')} {failure.row}: {failure.policyNumber}
                        </p>
                        <p className="text-sm text-red-600">{failure.error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setStep('select');
                    setSelectedFile(null);
                    setValidationResult(null);
                    setUploadResult(null);
                  }}
                  className="btn-secondary"
                >
                  {t('bulkUpload.uploadAnother')}
                </button>
                <button onClick={handleClose} className="btn-primary">
                  {t('common.done')}
                </button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

